import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { ApiError } from './api/client';
import Ornament from './components/Ornament';
import Icon from './components/Icon';
import './Signin.css';

const SignUp = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setError('');
        setSuccess('');

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        setIsSubmitting(true);
        try {
            await api.post('/api/auth/register', formData);
            setSuccess('Registration successful! Redirecting to sign in…');
            setFormData({ name: '', email: '', password: '' });
            setTimeout(() => navigate('/signin'), 900);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Unable to reach the server. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-card">
                <div className="auth-card-header">
                    <span className="swadharma-seal auth-seal" aria-hidden="true">ॐ</span>
                    <Ornament variant="rule" tone="gold" size={140} className="auth-rule" />
                    <h1>Create your account</h1>
                    <p className="sw-text-muted">Join the Samudyoga network</p>
                </div>

                <form className="auth-form" onSubmit={handleSubmit}>
                    <div className="auth-field">
                        <label htmlFor="name">Full name</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            placeholder="Your full name"
                            value={formData.name}
                            onChange={handleChange}
                            autoComplete="name"
                        />
                    </div>

                    <div className="auth-field">
                        <label htmlFor="email">Email address</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            placeholder="you@example.com"
                            value={formData.email}
                            onChange={handleChange}
                            autoComplete="email"
                        />
                    </div>

                    <div className="auth-field">
                        <label htmlFor="password">Password</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            required
                            minLength={8}
                            placeholder="At least 8 characters"
                            value={formData.password}
                            onChange={handleChange}
                            autoComplete="new-password"
                        />
                    </div>

                    <p className="sw-text-muted" style={{ fontSize: 13, margin: 0 }}>
                        Recruiter / institution accounts are provisioned separately —
                        contact the Samudyoga team to partner as an institution.
                    </p>

                    {error && <div className="auth-error" role="alert">{error}</div>}
                    {success && <div className="auth-success" role="status">{success}</div>}

                    <button
                        type="submit"
                        className="sw-btn sw-btn-saffron sw-btn-lg auth-submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Creating account…' : 'Create account'}
                    </button>

                    <p className="auth-alt">
                        Already have an account? <Link to="/signin">Sign in</Link>
                    </p>
                </form>
            </div>

            <aside className="auth-aside">
                <img
                    src="/samudyoga-logo.png"
                    alt="Samudyoga · A Dharma Poshanam initiative"
                    className="auth-aside-logo"
                />
                <div className="auth-aside-inner">
                    <span className="auth-aside-eyebrow">Connect · Engage · Empower</span>
                    <h2>Free to join.<br />Tradition at the core.</h2>
                    <ul className="auth-aside-list">
                        <li><Icon name="check" size={16} /> No fees for candidates — ever</li>
                        <li><Icon name="check" size={16} /> Institutions verified by our team</li>
                        <li><Icon name="check" size={16} /> Your data stays yours; opt-in only</li>
                    </ul>
                    <span className="auth-aside-tagline">
                        A Dharma Poshanam initiative · under Swadharma
                    </span>
                </div>
            </aside>
        </div>
    );
};

export default SignUp;
